/* 
Copyright (c) Karsten Jeske and John Firth
This source code is licensed under the proprietary license found in the
LICENSE file in the root directory of this source tree.
*/

import {
  CurrentUserParameters,
  UserParametersV1,
} from "features/userParameters/UserParametersSchemata"
import { getValueByLabel } from "services/workbooks/utils"
import { parseWorkbookV1 } from "services/workbooks/v1/workbookParsing"
import { parseWorkbookV2 } from "services/workbooks/v2/workbookParsing"
import * as XLSX from "xlsx"

// AIDEV-NOTE: Mark file input readonly to keep parse entrypoint side-effect free by contract.
export const parseWorkbookFile = async (
  file: Readonly<File>
): Promise<UserParametersV1 | CurrentUserParameters | string[]> => {
  const arrayBuffer = await file.arrayBuffer()
  const workbook = XLSX.read(arrayBuffer, {
    cellDates: true, // Parse dates as Date objects
    cellStyles: true,
    cellText: false, // Don't use the formatted text
    dateNF: "m/yyyy", // Preferred date format for parsing
  })

  if (!workbook.SheetNames.includes("Meta Data")) {
    return ["Missing required sheet: Meta Data."]
  }

  const metaDataSheet = workbook.Sheets["Meta Data"]

  const metaDataArray = XLSX.utils.sheet_to_json<(string | number | null)[]>(metaDataSheet, {
    header: 1,
    blankrows: false,
    defval: null,
    raw: true,
  })

  const versionValue = getValueByLabel(metaDataArray, "API version")

  if (versionValue === null) {
    return ["Missing API version in Meta Data sheet"]
  }

  if (typeof versionValue !== "number" && isNaN(Number(versionValue))) {
    return [`Invalid API version in Meta Data sheet: ${versionValue.toString()}. Must be a number.`]
  }

  const apiVersion = Number(versionValue)

  if (apiVersion === 1) {
    return parseWorkbookV1(workbook)
  } else if (apiVersion === 2) {
    return parseWorkbookV2(workbook)
  } else {
    return [`Unsupported API version in Meta Data sheet: ${apiVersion}`]
  }
}
